import { useState } from "react";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";

/**
 * @typedef {Object} Institution
 * @property {number} id
 * @property {string} name
 * @property {string|null} image_url
 */

/**
 * @typedef {Object} Agreement
 * @property {number} id
 * @property {string} tenant_id
 * @property {number} institution_id
 * @property {string} agreement_code
 * @property {number} duration_value
 * @property {string} duration_unit
 * @property {number} base_licenses
 * @property {string} price_per_unit
 * @property {string} billing_mode
 * @property {string} status
 * @property {Institution} institution
 */

/**
 * Admin - Get All School Agreements with server-side pagination & filtering
 */
export const useGetAllAgreements = () => {
  const { fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [agreements, setAgreements] = useState([]);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAgreements = async ({
    page = 1,
    per_page = ITEMS_PER_PAGE,
    filters = {},
    sort = "",
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("per_page", String(per_page));

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          query.append(`filter[${key}]`, String(value));
        }
      });

      if (sort) query.append("sort", sort);

      const response = await fetchWithAuth(
        `${apiUrl}/school/agreements?${query.toString()}`,
        { method: "GET" },
      );

      if (!response.ok) throw new Error("Failed to fetch agreements");

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to fetch agreements");
      }

      setAgreements(result.data || []);
      setMeta(result.meta || null);
      setStats(result.stats || null);
    } catch (err) {
      console.error("Agreement Fetch Error:", err);
      setAgreements([]);
      setMeta(null);
      setStats(null);
      setError(err?.message || ERROR_MESSAGES.UNKNOWN);
    } finally {
      setLoading(false);
    }
  };

  return {
    agreements,
    meta,
    stats,
    loading,
    error,
    fetchAgreements,
  };
};
